import { useState } from "react";
import { Users, BookOpen, ClipboardList, Download, CheckCircle, Clock, TrendingUp, LogOut, Plus, Trash2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { stats } from "../data/mockData";
import api from "../services/api";

// SECURITY: Admin password loaded from environment variable (set in .env)
// Never hardcode credentials in source code.
const ADMIN_CREDS = {
  username: "admin",
  // Falls back to empty string if env var not set — will require proper config in production
  password: process.env.REACT_APP_ADMIN_PASSWORD || "",
};

const LoginScreen = ({ onLogin }) => {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [show, setShow] = useState(false); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.loginAdmin(u, p);
      onLogin();
    } catch (e) {
      setErr(e.response?.data?.detail || "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F5F6] px-4" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#007CB7] rounded-xl flex items-center justify-center mx-auto mb-3"><ShieldCheck size={28} color="white" strokeWidth={1.5} /></div>
          <h1 className="text-xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>เข้าสู่ระบบผู้ดูแล</h1>
          <p className="text-sm text-[#5E636E]">วิทยาลัยนวัตกรรมสื่อสารสังคม มศว</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">ชื่อผู้ใช้</label>
            <input data-testid="admin-username-input" value={u} onChange={(e) => { setU(e.target.value); setErr(""); }} placeholder="กรอกชื่อผู้ใช้" className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1A1B1F] mb-1.5">รหัสผ่าน</label>
            <div className="relative">
              <input data-testid="admin-password-input" type={show ? "text" : "password"} value={p} onChange={(e) => { setP(e.target.value); setErr(""); }} placeholder="กรอกรหัสผ่าน" className="w-full border border-gray-300 rounded-md px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{show ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}</button>
            </div>
          </div>
          {err && <div data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#007CB7] hover:bg-[#005F8C] text-white py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-60">
            {loading ? "กำลังเข้าระบบ..." : "เข้าสู่ระบบ"}
          </button>
        </form>
      </div>
    </div>
  );
};

const AddTeacherForm = ({ onAdd, onCancel }) => {
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "" });
  const [show, setShow] = useState(false);
  const ok = form.name && form.username && form.password;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-4 fade-in-up">
      <h3 className="font-semibold text-[#1A1B1F] flex items-center gap-2" style={{ fontFamily: "'Prompt', sans-serif" }}><Plus size={16} strokeWidth={1.5} className="text-[#007CB7]" />เพิ่มอาจารย์ใหม่</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">ชื่ออาจารย์ *</label><input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} placeholder="เช่น รศ.ดร.สมชาย วงศ์ประภา" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
        <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">อีเมล</label><input value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} placeholder="example@swu.ac.th" type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
        <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">ชื่อผู้ใช้ *</label><input value={form.username} onChange={(e) => setForm(p => ({...p, username: e.target.value}))} placeholder="เช่น somchai_sc" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" /></div>
        <div><label className="block text-xs font-medium text-[#1A1B1F] mb-1">รหัสผ่าน *</label>
          <div className="relative"><input value={form.password} onChange={(e) => setForm(p => ({...p, password: e.target.value}))} type={show ? "text" : "password"} placeholder="อย่างน้อย 6 ตัวอักษร" className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm focus:ring-2 focus:ring-[#007CB7] focus:border-transparent outline-none" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">{show ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}</button>
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-[#5E636E] hover:bg-gray-50">ยกเลิก</button>
        <button onClick={() => ok && onAdd({ id: Date.now(), ...form, courses: 0, status: "ใช้งาน" })} disabled={!ok} data-testid="confirm-add-teacher-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium ${ok ? 'bg-[#007CB7] hover:bg-[#005F8C] text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
          <Plus size={14} strokeWidth={1.5} />เพิ่มอาจารย์
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm fade-in-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#5E636E]">{label}</p>
        <p className="text-3xl font-bold text-[#1A1B1F] mt-1" style={{ fontFamily: "'Prompt', sans-serif" }}>{value}</p>
        {sub && <p className="text-xs text-[#8A92A3] mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={20} strokeWidth={1.5} className="text-white" />
      </div>
    </div>
  </div>
);

export default function AdminView({ loggedIn, onLogin, onLogout, teachers, setTeachers, courses, setCourses, savedExams = [], setSavedExams }) {
  const [activeTab, setActiveTab] = useState("courses");
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteCourseConfirm, setDeleteCourseConfirm] = useState(null);
  const [deleteExamConfirm, setDeleteExamConfirm] = useState(null);

  if (!loggedIn) return <LoginScreen onLogin={onLogin} />;

  const handleExport = () => { setExportSuccess(true); setTimeout(() => setExportSuccess(false), 3000); };
  const handleAddTeacher = async (teacher) => {
    try {
      const res = await api.addTeacher(teacher);
      setTeachers(prev => [...prev, res.data]);
      setShowAddTeacher(false);
      toast.success("เพิ่มอาจารย์สำเร็จ");
    } catch (err) {
      toast.error(err.response?.data?.detail || "เพิ่มอาจารย์ไม่สำเร็จ");
    }
  };
  const handleDeleteTeacher = async (id) => {
    try {
      await api.deleteTeacher(id);
      setTeachers(prev => prev.filter(t => t.id !== id));
      setDeleteConfirm(null);
      toast.success("ลบอาจารย์สำเร็จ");
    } catch (err) {
      toast.error("ลบไม่สำเร็จ");
    }
  };
  const handleDeleteCourse = async (id) => {
    try {
      await api.deleteCourse(id);
      setCourses(prev => prev.filter(c => c.id !== id));
      setDeleteCourseConfirm(null);
      toast.success("ลบรายวิชาสำเร็จ");
    } catch (err) {
      toast.error("ลบไม่สำเร็จ");
    }
  };
  const handleDeleteExam = async (id) => {
    try {
      await api.deleteExam(id);
      setSavedExams(prev => prev.filter(e => e.id !== id));
      setDeleteExamConfirm(null);
      toast.success("ลบข้อสอบสำเร็จ");
    } catch (err) {
      toast.error("ลบข้อสอบไม่สำเร็จ");
    }
  };
  const totalStudents = courses.reduce((sum, c) => sum + (Number(c.students) || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8" style={{ fontFamily: "'Noto Sans Thai', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1B1F]" style={{ fontFamily: "'Prompt', sans-serif" }}>แผงควบคุมผู้ดูแลระบบ</h1>
          <p className="text-[#5E636E] text-sm mt-1">ภาคการศึกษาที่ 1 ปีการศึกษา 2567 — วิทยาลัยนวัตกรรมสื่อสารสังคม มศว</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-scores-btn" onClick={handleExport} className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm">
            <Download size={15} strokeWidth={1.5} />Export คะแนน
          </button>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-[#5E636E] px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <LogOut size={14} strokeWidth={1.5} />ออกจากระบบ
          </button>
        </div>
      </div>

      {exportSuccess && (
        <div data-testid="export-success-alert" className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-md px-4 py-3 text-sm fade-in-up">
          <CheckCircle size={16} strokeWidth={1.5} />ส่งออกข้อมูลคะแนนสำเร็จ
        </div>
      )}

      {/* Stats - uses live savedExams data */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Users} label="จำนวนนิสิตทั้งหมด" value={totalStudents} sub="ลงทะเบียนในระบบ" color="bg-[#007CB7]" />
        <StatCard icon={BookOpen} label="จำนวนรายวิชา" value={courses.length} sub="ภาคการศึกษานี้" color="bg-emerald-500" />
        <StatCard icon={ClipboardList} label="การสอบที่เปิดอยู่" value={savedExams.filter(e => e.status !== "ปิดสอบ").length} sub={`จากทั้งหมด ${savedExams.length} การสอบ`} color="bg-amber-500" />
      </div>

      {/* Active Exams - live from savedExams */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <Clock size={16} strokeWidth={1.5} className="text-[#007CB7]" />
          <h2 className="font-semibold text-[#1A1B1F] text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>การสอบทั้งหมดในระบบ</h2>
        </div>
        <div className="overflow-x-auto">
          {savedExams.length === 0 ? (
            <div className="px-5 py-8 text-center text-[#8A92A3] text-sm">ยังไม่มีข้อสอบในระบบ — อาจารย์สามารถสร้างข้อสอบได้ในส่วนอาจารย์ผู้สอน</div>
          ) : (
            <table className="w-full text-sm" data-testid="active-exams-table">
              <thead><tr className="bg-gray-50 text-[#5E636E] text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-semibold">ชื่อการสอบ</th>
                <th className="text-left px-5 py-3 font-semibold">รายวิชา</th>
                <th className="text-left px-5 py-3 font-semibold">อาจารย์</th>
                <th className="text-left px-5 py-3 font-semibold">จำนวนข้อ</th>
                <th className="text-left px-5 py-3 font-semibold">สถานะ</th>
                <th className="text-left px-5 py-3 font-semibold">การจัดการ</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {savedExams.map((exam) => (
                  <tr key={exam.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3.5 text-[#1A1B1F] font-medium">{exam.title}</td>
                    <td className="px-5 py-3.5 text-[#5E636E]">{exam.courseName || exam.course}</td>
                    <td className="px-5 py-3.5 text-[#5E636E] text-xs">{exam.teacherName}</td>
                    <td className="px-5 py-3.5 text-[#5E636E]">{exam.questions?.length} ข้อ</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        exam.status === "เปิดสอบ" ? "bg-green-100 text-green-700" :
                        exam.status === "ปิดชั่วคราว" ? "bg-amber-100 text-amber-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>{exam.status || "เปิดสอบ"}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      {deleteExamConfirm === exam.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600">ยืนยันลบ?</span>
                          <button onClick={() => handleDeleteExam(exam.id)} className="text-xs text-red-600 font-medium hover:text-red-800">ยืนยัน</button>
                          <button onClick={() => setDeleteExamConfirm(null)} className="text-xs text-gray-500">ยกเลิก</button>
                        </div>
                      ) : (
                        <button data-testid={`delete-exam-${exam.id}`} onClick={() => setDeleteExamConfirm(exam.id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors">
                          <Trash2 size={12} strokeWidth={1.5} />ลบ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Course & Teacher Management */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} strokeWidth={1.5} className="text-[#007CB7]" />
              <h2 className="font-semibold text-[#1A1B1F] text-base" style={{ fontFamily: "'Prompt', sans-serif" }}>จัดการข้อมูลวิชาและอาจารย์</h2>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'teachers' && (
                <button data-testid="add-teacher-toggle-btn" onClick={() => setShowAddTeacher(!showAddTeacher)}
                  className="flex items-center gap-2 bg-[#007CB7] hover:bg-[#005F8C] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
                  <Plus size={13} strokeWidth={1.5} />เพิ่มอาจารย์
                </button>
              )}
              <div className="flex items-center bg-gray-100 p-0.5 rounded-md">
                <button data-testid="tab-courses" onClick={() => { setActiveTab("courses"); setShowAddTeacher(false); }} className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${activeTab === "courses" ? "bg-white text-[#007CB7] shadow-sm" : "text-gray-500"}`}>รายวิชา</button>
                <button data-testid="tab-teachers" onClick={() => setActiveTab("teachers")} className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${activeTab === "teachers" ? "bg-white text-[#007CB7] shadow-sm" : "text-gray-500"}`}>อาจารย์</button>
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'teachers' && showAddTeacher && (
          <div className="px-5 py-4 border-b border-gray-100">
            <AddTeacherForm onAdd={handleAddTeacher} onCancel={() => setShowAddTeacher(false)} />
          </div>
        )}

        <div className="overflow-x-auto">
          {activeTab === "courses" ? (
            <table className="w-full text-sm" data-testid="courses-table">
              <thead><tr className="bg-gray-50 text-[#5E636E] text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-semibold">รหัสวิชา</th>
                <th className="text-left px-5 py-3 font-semibold">ชื่อรายวิชา</th>
                <th className="text-left px-5 py-3 font-semibold">อาจารย์ผู้สอน</th>
                <th className="text-left px-5 py-3 font-semibold">จำนวนนิสิต</th>
                <th className="text-left px-5 py-3 font-semibold">สถานะ</th>
                <th className="text-left px-5 py-3 font-semibold">การจัดการ</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {courses.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3.5 font-mono text-xs text-[#007CB7] font-semibold">{c.id}</td>
                    <td className="px-5 py-3.5 text-[#1A1B1F] font-medium">{c.name}</td>
                    <td className="px-5 py-3.5 text-[#5E636E]">{c.teacher}</td>
                    <td className="px-5 py-3.5 text-[#5E636E]">{c.students} คน</td>
                    <td className="px-5 py-3.5"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{c.status}</span></td>
                    <td className="px-5 py-3.5">
                      {deleteCourseConfirm === c.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600">ยืนยันลบ?</span>
                          <button onClick={() => handleDeleteCourse(c.id)} className="text-xs text-red-600 font-medium hover:text-red-800">ยืนยัน</button>
                          <button onClick={() => setDeleteCourseConfirm(null)} className="text-xs text-gray-500">ยกเลิก</button>
                        </div>
                      ) : (
                        <button data-testid={`delete-course-admin-${c.id}`} onClick={() => setDeleteCourseConfirm(c.id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors">
                          <Trash2 size={12} strokeWidth={1.5} />ลบ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm" data-testid="teachers-table">
              <thead><tr className="bg-gray-50 text-[#5E636E] text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-semibold">ชื่ออาจารย์</th>
                <th className="text-left px-5 py-3 font-semibold">ชื่อผู้ใช้</th>
                <th className="text-left px-5 py-3 font-semibold">อีเมล</th>
                <th className="text-left px-5 py-3 font-semibold">สถานะ</th>
                <th className="text-left px-5 py-3 font-semibold">การจัดการ</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {teachers.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3.5 text-[#1A1B1F] font-medium">{t.name}</td>
                    <td className="px-5 py-3.5 font-mono text-xs text-[#007CB7]">{t.username}</td>
                    <td className="px-5 py-3.5 text-[#5E636E] font-mono text-xs">{t.email}</td>
                    <td className="px-5 py-3.5"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{t.status}</span></td>
                    <td className="px-5 py-3.5">
                      {deleteConfirm === t.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600">ยืนยันลบ?</span>
                          <button onClick={() => handleDeleteTeacher(t.id)} className="text-xs text-red-600 hover:text-red-800 font-medium">ยืนยัน</button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-500">ยกเลิก</button>
                        </div>
                      ) : (
                        <button data-testid={`delete-teacher-${t.id}`} onClick={() => setDeleteConfirm(t.id)}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors">
                          <Trash2 size={12} strokeWidth={1.5} />ลบ
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

